/**
 * OptiDVI Panel - Digital Vehicle Inspection for tickets
 * 
 * Phase 4 Features:
 * - Template-driven inspection checklist
 * - Upload photos/videos (path: {shopId}/{ticketId}/{uuid}.{ext})
 * - Auto-group media into Finding Groups
 * - Multi-image AI analysis per group
 * - Estimate draft builder with apply-to-ticket
 * - Session management + customer sharing
 */

import { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useOptiDvi } from '@/hooks/useOptiDvi';
import { useDviSession } from '@/hooks/useDviSession';
import { useDviFindingGroups } from '@/hooks/useDviFindingGroups';
import { useDviEstimate } from '@/hooks/useDviEstimate';
import { useTicketInspection } from '@/hooks/useTicketInspection';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useToast } from '@/hooks/use-toast';
import { 
  Upload, 
  Loader2, 
  Share2, 
  ThumbsUp, 
  ThumbsDown, 
  Clock, 
  Layers, 
  FileText,
  Sparkles,
  Plus,
  ClipboardCheck,
  Maximize2
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { FindingGroupCard } from '@/components/dvi/FindingGroupCard';
import { EstimateDraftPanel } from '@/components/dvi/EstimateDraftPanel';
import { InspectionChecklistPanel } from '@/components/inspections/InspectionChecklistPanel';
import { DviWorkflowModal } from '@/components/dvi/DviWorkflowModal';

interface TicketDviPanelProps {
  ticketId: string;
  shopId: string;
  vehicle?: {
    year?: number;
    make?: string;
    model?: string;
  };
  onJobsAdded?: () => void;
}

const SESSION_STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  draft: { label: 'Draft', className: 'bg-muted text-muted-foreground' },
  shared: { label: 'Shared', className: 'bg-blue-500/10 text-blue-600' },
  customer_viewed: { label: 'Viewed', className: 'bg-purple-500/10 text-purple-600' },
  customer_responded: { label: 'Responded', className: 'bg-green-500/10 text-green-600' },
  archived: { label: 'Archived', className: 'bg-muted text-muted-foreground' },
};

export function TicketDviPanel({ ticketId, shopId, vehicle, onJobsAdded }: TicketDviPanelProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const dvi = useOptiDvi();
  const dviSession = useDviSession(ticketId, shopId);
  const sessionId = dviSession.session?.id || null;
  const findingGroups = useDviFindingGroups(sessionId, ticketId);
  const estimate = useDviEstimate(sessionId, ticketId);
  const ticketInspection = useTicketInspection(ticketId, shopId, 'DVI');
  
  const [isUploading, setIsUploading] = useState(false);
  const [workflowOpen, setWorkflowOpen] = useState(false);
  
  // Outer tabs: Inspection Checklist vs Customer Findings
  // Default tab logic with safe optional chaining (Fix #5)
  const defaultOuterTab = useMemo(() => {
    if (ticketInspection.inspection?.status === 'IN_PROGRESS') return 'checklist';
    if (dviSession?.session != null) return 'findings';
    if ((findingGroups?.findingGroups?.length ?? 0) > 0) return 'findings';
    return 'checklist';
  }, [ticketInspection.inspection?.status, dviSession?.session, findingGroups?.findingGroups?.length]);
  
  const [outerTab, setOuterTab] = useState<'checklist' | 'findings'>(defaultOuterTab);
  const [activeTab, setActiveTab] = useState<'findings' | 'estimate'>('findings');

  // Fetch DVI media count for this ticket
  const { data: mediaCount = 0 } = useQuery({
    queryKey: ['dvi-media-count', ticketId],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('ticket_dvi_media')
        .select('id', { count: 'exact', head: true })
        .eq('ticket_id', ticketId);
      if (error) throw error;
      return count || 0;
    },
    enabled: dvi.isEnabled,
  });

  // Fetch ungrouped media count
  const { data: ungroupedCount = 0 } = useQuery({
    queryKey: ['dvi-media-ungrouped', ticketId],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('ticket_dvi_media')
        .select('id', { count: 'exact', head: true })
        .eq('ticket_id', ticketId)
        .is('finding_group_id', null);
      if (error) throw error;
      return count || 0;
    },
    enabled: dvi.isEnabled && !!sessionId,
  });

  // Upload mutation - multi-file with camera support
  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    if (files.length === 0) return;

    // Clear input so re-selecting same file(s) triggers onChange again
    event.target.value = '';

    setIsUploading(true);

    try {
      // Ensure session exists - capture session_id from return value (not stale state)
      let currentSessionId = sessionId;

      if (!currentSessionId) {
        const created = await dviSession.createSession();
        currentSessionId = created?.session_id ?? null;
      }

      if (!currentSessionId) {
        throw new Error('Unable to create or load a DVI session. Please try again.');
      }

      let successCount = 0;
      let failCount = 0;

      for (const file of files) {
        const isImage = file.type.startsWith('image/');
        const isVideo = file.type.startsWith('video/');

        if (!isImage && !isVideo) {
          failCount += 1;
          continue;
        }

        try {
          // CRITICAL: Path format must be {shopId}/{ticketId}/{uuid}.{ext} for RLS
          const uuid = crypto.randomUUID();
          const ext = file.name.split('.').pop() || (isImage ? 'jpg' : 'mp4');
          const storagePath = `${shopId}/${ticketId}/${uuid}.${ext}`;

          // Upload to storage (UUID path prevents collisions)
          const { error: uploadError } = await supabase.storage
            .from('dvi_media')
            .upload(storagePath, file, { upsert: false });

          if (uploadError) throw uploadError;

          // Create media record
          const { error: insertError } = await supabase
            .from('ticket_dvi_media')
            .insert({
              shop_id: shopId,
              ticket_id: ticketId,
              session_id: currentSessionId,
              storage_path: storagePath,
              media_type: isImage ? 'image' : 'video',
              captured_by_user_id: user?.id,
              created_by_user_id: user?.id,
            });

          if (insertError) throw insertError;

          successCount += 1;
        } catch (err) {
          console.warn('[DVI Upload] File failed:', file.name, err);
          failCount += 1;
        }
      }

      // Refresh UI once at end
      queryClient.invalidateQueries({ queryKey: ['dvi-media-count', ticketId] });
      queryClient.invalidateQueries({ queryKey: ['dvi-media-ungrouped', ticketId] });

      // Summary toast (single toast for whole batch)
      if (successCount > 0 && failCount === 0) {
        toast({ title: 'Uploaded', description: `${successCount} file(s) uploaded successfully` });
      } else if (successCount > 0 && failCount > 0) {
        toast({
          title: 'Partial upload',
          description: `${successCount} uploaded, ${failCount} failed or unsupported`,
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Upload failed',
          description: 'No valid image/video files were uploaded.',
          variant: 'destructive',
        });
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Upload failed';
      toast({ title: 'Upload failed', description: message, variant: 'destructive' });
    } finally {
      setIsUploading(false);
    }
  };

  if (!dvi.isEnabled) return null;

  const sessionStatus = dviSession.session?.status;
  const statusConfig = sessionStatus ? SESSION_STATUS_CONFIG[sessionStatus] : null;
  const approvedCount = findingGroups.approvedByCustomer.length;
  const hasAnalyzedGroups = findingGroups.analyzedGroups.length > 0;

  return (
    <div className="space-y-3 p-2">
      {/* Outer Tabs: Inspection Checklist vs Customer Findings */}
      <Tabs value={outerTab} onValueChange={(v) => setOuterTab(v as 'checklist' | 'findings')}>
        <TabsList className="grid w-full grid-cols-2 h-9">
          <TabsTrigger value="checklist" className="text-xs gap-1.5">
            <ClipboardCheck className="h-3.5 w-3.5" />
            Inspection Checklist
          </TabsTrigger>
          <TabsTrigger value="findings" className="text-xs gap-1.5">
            <Sparkles className="h-3.5 w-3.5" />
            Customer Findings
          </TabsTrigger>
        </TabsList>
        
        {/* Inspection Checklist Tab */}
        <TabsContent value="checklist" className="mt-3">
          <InspectionChecklistPanel
            ticketId={ticketId}
            shopId={shopId}
            vehicle={vehicle}
          />
        </TabsContent>
        
        {/* Customer Findings Tab (existing content) */}
        <TabsContent value="findings" className="mt-3 space-y-3">
          {/* Session Management Bar */}
          <div className="flex items-center gap-2 flex-wrap">
            {dviSession.session ? (
              <>
                {statusConfig && (
                  <Badge variant="outline" className={statusConfig.className}>
                    {statusConfig.label}
                  </Badge>
                )}
            {mediaCount > 0 && (
              <Badge variant="secondary" className="text-xs">
                {mediaCount} media
              </Badge>
            )}
            {dvi.canApprove && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    onClick={() => dviSession.shareSession()}
                    disabled={dviSession.isSharing}
                  >
                    {dviSession.isSharing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Share2 className="h-3 w-3" />}
                    Share
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Generate customer link (invalidates previous)</p>
                </TooltipContent>
              </Tooltip>
            )}
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => setWorkflowOpen(true)}
            >
              <Maximize2 className="h-3 w-3" />
              Full Workflow
            </Button>
          </>
        ) : (
          dvi.canApprove && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => dviSession.createSession()}
              disabled={dviSession.isCreatingSession}
            >
              {dviSession.isCreatingSession ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
              Create Session
            </Button>
          )
        )}
      </div>

      {/* Customer Decision Summary */}
      {sessionStatus === 'customer_responded' && (
        <div className="flex gap-2 text-xs">
          {approvedCount > 0 && (
            <span className="flex items-center gap-1 text-green-600">
              <ThumbsUp className="h-3 w-3" /> {approvedCount} approved
            </span>
          )}
          {findingGroups.declinedByCustomer.length > 0 && (
            <span className="flex items-center gap-1 text-red-600">
              <ThumbsDown className="h-3 w-3" /> {findingGroups.declinedByCustomer.length} declined
            </span>
          )}
        </div>
      )}

      {/* Upload + Auto-Group Actions */}
      <div className="flex gap-2 flex-wrap">
        {dvi.canUpload && (
          <Button variant="outline" size="sm" className="gap-2" asChild disabled={isUploading}>
            <label>
              {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              Upload Photo/Video
              <input
                type="file"
                accept="image/*,video/*"
                capture="environment"
                multiple
                onChange={handleUpload}
                className="hidden"
              />
            </label>
          </Button>
        )}

        {ungroupedCount > 0 && sessionId && (
          <Button
            variant="secondary"
            size="sm"
            className="gap-1.5"
            onClick={() => findingGroups.autoGroup()}
            disabled={findingGroups.isAutoGrouping}
          >
            {findingGroups.isAutoGrouping ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Layers className="h-3 w-3" />
            )}
            Group {ungroupedCount} Media
          </Button>
        )}

        {hasAnalyzedGroups && dvi.canAddToTicket && !estimate.currentDraft && (
          <Button
            variant="secondary"
            size="sm"
            className="gap-1.5"
            onClick={() => estimate.buildDraft('yellow_red')}
            disabled={estimate.isBuildingDraft}
          >
            {estimate.isBuildingDraft ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <FileText className="h-3 w-3" />
            )}
            Build Estimate
          </Button>
        )}
      </div>

      {/* Tabs: Findings / Estimate */}
      {sessionId && (findingGroups.findingGroups.length > 0 || estimate.currentDraft) && (
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'findings' | 'estimate')}>
          <TabsList className="grid w-full grid-cols-2 h-8">
            <TabsTrigger value="findings" className="text-xs gap-1">
              <Sparkles className="h-3 w-3" />
              Findings ({findingGroups.findingGroups.length})
            </TabsTrigger>
            <TabsTrigger value="estimate" className="text-xs gap-1" disabled={!estimate.currentDraft}>
              <FileText className="h-3 w-3" />
              Estimate {estimate.currentDraft ? `(${estimate.unappliedItems.length})` : ''}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="findings" className="mt-3 space-y-2">
            {findingGroups.isLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading findings...
              </div>
            ) : findingGroups.findingGroups.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4">
                No finding groups yet. Upload media and click "Group" to get started.
              </p>
            ) : (
              <>
                {/* Red findings first */}
                {findingGroups.redGroups.map((group) => (
                  <FindingGroupCard
                    key={group.id}
                    group={group}
                    onAnalyze={() => findingGroups.analyzeGroup(group.id)}
                    isAnalyzing={findingGroups.isAnalyzing && findingGroups.analyzingGroupId === group.id}
                    showCustomerDecision={sessionStatus === 'customer_responded'}
                  />
                ))}
                {/* Yellow findings */}
                {findingGroups.yellowGroups.map((group) => (
                  <FindingGroupCard
                    key={group.id}
                    group={group}
                    onAnalyze={() => findingGroups.analyzeGroup(group.id)}
                    isAnalyzing={findingGroups.isAnalyzing && findingGroups.analyzingGroupId === group.id}
                    showCustomerDecision={sessionStatus === 'customer_responded'}
                  />
                ))}
                {/* Green findings */}
                {findingGroups.greenGroups.map((group) => (
                  <FindingGroupCard
                    key={group.id}
                    group={group}
                    onAnalyze={() => findingGroups.analyzeGroup(group.id)}
                    isAnalyzing={findingGroups.isAnalyzing && findingGroups.analyzingGroupId === group.id}
                    showCustomerDecision={sessionStatus === 'customer_responded'}
                    compact
                  />
                ))}
                {/* Pending (not yet analyzed) */}
                {findingGroups.pendingGroups.map((group) => (
                  <FindingGroupCard
                    key={group.id}
                    group={group}
                    onAnalyze={() => findingGroups.analyzeGroup(group.id)}
                    isAnalyzing={findingGroups.isAnalyzing && findingGroups.analyzingGroupId === group.id}
                  />
                ))}
              </>
            )}
          </TabsContent>

          <TabsContent value="estimate" className="mt-3">
            {estimate.currentDraft ? (
              <EstimateDraftPanel
                items={estimate.draftItems}
                totalLaborMinutes={estimate.totalLaborMinutes}
                onApplySelected={(ids) => estimate.applyItems(ids)}
                onApplyAll={() => estimate.applyAllApproved()}
                isApplying={estimate.isApplying}
                onApproveItems={(ids) => estimate.approveItems(ids)}
                onRejectItems={(ids, reason) => estimate.rejectItems(ids, reason)}
                isReviewing={estimate.isReviewing}
                onJobsAdded={onJobsAdded}
              />
            ) : (
              <p className="text-sm text-muted-foreground py-4">
                No estimate draft. Analyze findings first, then click "Build Estimate".
              </p>
            )}
          </TabsContent>
        </Tabs>
          )}

          {/* Empty state */}
          {!sessionId && mediaCount === 0 && (
            <p className="text-sm text-muted-foreground">No inspection media yet. Upload photos to get started.</p>
          )}
        </TabsContent>
      </Tabs>

      {/* DVI Workflow Modal */}
      <DviWorkflowModal
        open={workflowOpen}
        onOpenChange={setWorkflowOpen}
        ticketId={ticketId}
        shopId={shopId}
        vehicle={vehicle}
        onJobsAdded={onJobsAdded}
      />
    </div>
  );
}
